/**
 * Shared IndexedDB glue — `openDb` + `tx`, the two helpers an IDB-backed store in
 * the frontend needs (one DB connection opened lazily; every operation in a
 * transaction that resolves on completion). Its consumer is `packStore.ts`, the
 * compendium pack store.
 *
 * Bun has no IndexedDB, but `fake-indexeddb` provides a spec-compliant
 * implementation under `bun test`, so these helpers get real round-trip coverage
 * through the stores built on them.
 */

/**
 * Open (or upgrade) a named IndexedDB database, reusing the connection across
 * calls is the caller's job (each store lazily memoizes its own `db()` promise).
 * `upgrade` runs on `onupgradeneeded` to create/migrate object stores.
 */
export function openDb(
  name: string,
  version: number,
  upgrade: (db: IDBDatabase) => void,
): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(name, version);
    req.onupgradeneeded = () => upgrade(req.result);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Run `fn` inside a transaction on `storeName` and resolve when the transaction
 * **completes** (not merely when the request succeeds — completion is what
 * guarantees the write is durable). The optional request `fn` returns its result.
 */
export type TxArgs<T> = {
  db: IDBDatabase;
  storeName: string;
  mode: IDBTransactionMode;
  fn: (store: IDBObjectStore) => IDBRequest<T> | void;
};

export function tx<T>(args: TxArgs<T>): Promise<T | undefined> {
  const { db, storeName, mode, fn } = args;
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    let result: T | undefined;
    const req = fn(store);
    if (req) req.onsuccess = () => (result = req.result);
    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
}
